import { useCallback, useMemo } from "react"
import { useWaitForTransactionReceipt, useWriteContract } from "wagmi"

import {
  MEZO_CHAIN,
  ONE_DAY_IN_SECONDS,
  ONE_WEEK_IN_SECONDS,
} from "#/shared/constants"
import { MezoNativeBTC } from "#/shared/constants/artifacts"

import VeBTCAbi, {
  address as veBTCAddress,
} from "../../constants/artifacts/VeBTC"
import { PastStakeActivity, StakingLockDurationType } from "../../types"
import { useApproveERC20Token } from "../erc-20"

import useStakes from "./useStakes"

export { useStakes }
export { default as useIncreaseStakeAmount } from "./useIncreaseStakeAmount"
export { default as useStakingStats } from "./useStakingStats"

// 4 years
const MAX_LOCK_SECONDS = BigInt(4 * 365 * ONE_DAY_IN_SECONDS)

export const useEstimateVeBTCValue = (
  amount: bigint,
  lockDuration: StakingLockDurationType,
) => {
  if (amount === 0n) return 0n

  const weeks = lockDuration / ONE_WEEK_IN_SECONDS
  const seconds = weeks * ONE_WEEK_IN_SECONDS

  const slope = amount / MAX_LOCK_SECONDS
  const bias = slope * BigInt(seconds)

  return bias
}

export const usePastStakes = () => [] as PastStakeActivity[]

export const useStake = (stakeId: string) => {
  const { data: stakes } = useStakes()

  return stakes?.find((stake) => stake.id === stakeId)
}

const initialStakingData = {
  totalClaimedMats: 0n,
  totalStaked: 0n,
  totalVotingPower: 0n,
}

export const useStakingData = () => {
  const { data: stakes, isFetching } = useStakes()

  const stakingData = useMemo(() => {
    if (!stakes) return initialStakingData

    return stakes?.reduce(
      (accumulator, stake) => {
        accumulator.totalStaked += stake.amount
        accumulator.totalVotingPower += stake.votingPower ?? 0n

        return accumulator
      },
      { ...initialStakingData },
    )
  }, [stakes])

  return { data: stakingData, isFetching }
}

export const useShowStakingSummary = () => {
  const { data: stakes } = useStakes()
  const pastStakes = usePastStakes()
  return (!!stakes && stakes?.length > 0) || !!pastStakes.length
}

type StakeBtcVariables = {
  lockDuration: StakingLockDurationType
  value: bigint
}

export const useStakeBtc = (shouldApproveIfNeeded = false) => {
  const { writeContractAsync, data: txHash, ...rest } = useWriteContract()
  const { approve } = useApproveERC20Token(MezoNativeBTC.address, veBTCAddress)

  const { isLoading: isWaitingForTxReceipt, isSuccess: isTxConfirmed } =
    useWaitForTransactionReceipt({
      hash: txHash,
      chainId: MEZO_CHAIN.id,
    })

  const stake = useCallback(
    async (
      { lockDuration, value }: StakeBtcVariables,
      options?: { onError?: (error: unknown) => void; onSuccess?: () => void },
    ) => {
      if (shouldApproveIfNeeded) await approve(value)

      await writeContractAsync(
        {
          abi: VeBTCAbi,
          address: veBTCAddress,
          functionName: "createLock",
          args: [value, BigInt(lockDuration)],
          chainId: MEZO_CHAIN.id,
        },
        options,
      )
    },
    [writeContractAsync, shouldApproveIfNeeded, approve],
  )

  return {
    ...rest,
    isWaitingForTxReceipt,
    isTxConfirmed,
    txHash,
    stake,
  }
}
